import { useTranslations } from "@zapier/i18n";
import {
  type HydratedCanvas,
  useBulkRestoreAssets,
  useBulkTrashAssets,
  useSplitTreatments,
} from "@zapier/client";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Spacer,
  Text,
} from "@zapier/design-system";
import { CANVAS_ASSET_ID_PREFIX, useNotification } from "@zapier/utils";

import { useDeleteCanvas } from "./useDeleteCanvas";
import { useEmitCanvasDeleteModalEvent } from "@zapier/shared/src/events";
import { FlexSpacer, TrashNotification } from "@zapier/shared";

interface Props {
  canvas: HydratedCanvas | undefined;
  isOpen: boolean;
  onClose: () => void;
  folderId?: string; // used for cache invalidation in mutations
}

export function CanvasDeleteModal({
  canvas,
  isOpen,
  onClose,
  folderId,
}: Props) {
  const t = useTranslations("canvas-delete-modal");
  const tFallbacks = useTranslations("fallbacks");
  const { isZapAndCanvasUnifiedTrashEnabled } = useSplitTreatments();
  const { createNotification, clearNotification } = useNotification();

  const { deleteCanvas, isPending } = useDeleteCanvas({ canvas, folderId });
  const bulkTrashAssets = useBulkTrashAssets({ folderId });
  const bulkRestoreAssets = useBulkRestoreAssets({ folderId });
  const { emit } = useEmitCanvasDeleteModalEvent();

  const handleUndoTrash = async (assetIds: string[], trashToastId: string) => {
    const canvasName =
      canvas?.name || tFallbacks("untitled-asset", { assetType: "Canvas" });
    try {
      // A resolved mutation is not a restored canvas: bulkRestore reports per-id
      // outcomes in its arrays rather than throwing on failure.
      const { failedAssetIds } = await bulkRestoreAssets.mutateAsync({
        assetIds,
      });

      if (failedAssetIds.length > 0) {
        createNotification({
          message: t("notifications.restore-error", { canvasName }),
          type: "error",
        });
        return;
      }

      clearNotification(trashToastId);
      createNotification({
        message: t("notifications.restore-success", { canvasName }),
        type: "success",
      });
    } catch {
      createNotification({
        message: t("notifications.restore-error", { canvasName }),
        type: "error",
      });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      emit("cancel");
      onClose();
    }
  };

  if (!canvas) {
    return;
  }

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={handleOpenChange}>
      <>
        <ModalDialogHeader>
          {t("header.delete-canvas-title", {
            trashEnabled: String(isZapAndCanvasUnifiedTrashEnabled),
            canvasName:
              canvas.name ||
              tFallbacks("untitled-asset", { assetType: "Canvas" }),
          })}
        </ModalDialogHeader>

        <ModalDialogBody>
          <Text color="TextWeaker">
            {t("body.deletion-warning", {
              trashEnabled: String(isZapAndCanvasUnifiedTrashEnabled),
            })}
          </Text>
          {isZapAndCanvasUnifiedTrashEnabled && (
            <>
              <Spacer height={12} />
              <Text color="TextWeaker">{t("body.connected-assets")}</Text>
            </>
          )}
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            isDisabled={isPending || bulkTrashAssets.isPending}
            onPress={() => {
              emit("cancel");
              onClose();
            }}
            variant="tertiary"
          >
            {t("buttons.cancel")}
          </Button>

          <FlexSpacer />

          <Button
            isDisabled={isPending || bulkTrashAssets.isPending}
            isPending={isPending || bulkTrashAssets.isPending}
            onPress={async () => {
              emit("confirm");
              if (isZapAndCanvasUnifiedTrashEnabled) {
                try {
                  const trashedIds = [`${CANVAS_ASSET_ID_PREFIX}${canvas.id}`];
                  const { failedAssetIds } = await bulkTrashAssets.mutateAsync({
                    assetIds: trashedIds,
                  });

                  if (failedAssetIds.length > 0) {
                    createNotification({
                      message: t("notifications.trash-error"),
                      type: "error",
                    });
                    return;
                  }

                  const trashToastId = createNotification({
                    title: t("notifications.trash-success", {
                      canvasName:
                        canvas.name ||
                        tFallbacks("untitled-asset", {
                          assetType: "Canvas",
                        }),
                    }),
                    message: (
                      <TrashNotification
                        onUndo={() => handleUndoTrash(trashedIds, trashToastId)}
                      />
                    ),
                    type: "success",
                  });
                  onClose();
                } catch {
                  createNotification({
                    message: t("notifications.trash-error"),
                    type: "error",
                  });
                }
              } else {
                deleteCanvas();
                onClose();
              }
            }}
            variant="danger"
          >
            {t("buttons.confirm-action", {
              trashEnabled: String(isZapAndCanvasUnifiedTrashEnabled),
            })}
          </Button>
        </ModalDialogFooter>
      </>
    </ModalDialogRoot>
  );
}

// For ease of use during `dynamic` import
export default CanvasDeleteModal;
